import { CATALOG, type CatalogVerse } from "../deck/catalog";
import {
  GALLERY_CHAPTER_PIECES,
  GALLERY_CHAPTER_SECTIONS,
  isArtUnlocked,
  loadCollection,
  VERSE_CATEGORY_ORDER,
  verseCategoryLabel,
  verseFacePiece,
  type GalleryPiece,
} from "./index";

document.documentElement.dataset.app = "menu";

loadCollection();

const catalogById = new Map(CATALOG.map((v) => [v.id, v]));
const pieceById = new Map<string, GalleryPiece>();

const versePieces: GalleryPiece[] = CATALOG.map((v) =>
  verseFacePiece(v.id, v.reference),
);

/** Flat list of every gallery piece (chapters + verses) for stats. */
const allPieces: GalleryPiece[] = [...GALLERY_CHAPTER_PIECES, ...versePieces];
for (const p of allPieces) pieceById.set(p.id, p);

const RECENT_LIMIT = 8;

/** Nav contexts for lightbox ← → within a section. */
type LightboxContext = {
  pieces: readonly GalleryPiece[];
  index: number;
};

let lightboxCtx: LightboxContext | null = null;

function verseForPiece(piece: GalleryPiece): CatalogVerse | undefined {
  const u = piece.unlock;
  if (u.type !== "own-verse") return undefined;
  return catalogById.get(u.verseId);
}

/** Friendly stage names for mission-clear gallery pieces. */
const MISSION_HINTS: Readonly<Record<string, string>> = {
  "neh-before-the-king": "Clear Before the King (Rebuild the Wall)",
  "neh-night-survey": "Clear The Night Survey (Rebuild the Wall)",
  "neh-mockery": "Clear Mockery (Rebuild the Wall)",
  "neh-sword-and-trowel": "Clear Sword and Trowel (Rebuild the Wall)",
  "gid-winepress": "Clear By the Winepress (The Three Hundred)",
  "gid-jehovah-shalom": "Clear Jehovah-shalom (The Three Hundred)",
  "gid-jerubbaal": "Clear Jerubbaal (The Three Hundred)",
  "gid-jezreel": "Clear Valley of Jezreel (The Three Hundred)",
  "gid-three-hundred": "Clear Three Hundred (The Three Hundred)",
};

const CHAPTER_HINTS: Readonly<Record<string, string>> = {
  "neh-wall": "Complete Rebuild the Wall (finale)",
  "gideon-300": "Complete The Three Hundred (finale)",
};

function hintFor(piece: GalleryPiece): string {
  const u = piece.unlock;
  if (u.type === "own-verse") {
    const v = catalogById.get(u.verseId);
    return v ? `Own the card · ${v.reference}` : "Own this verse card";
  }
  if (u.type === "mission-clear") {
    return (
      MISSION_HINTS[u.missionId] ?? "Clear the matching campaign stage"
    );
  }
  if (u.type === "chapter-finale") {
    if (piece.featured) {
      return (
        CHAPTER_HINTS[u.chapterId] ??
        "Complete the campaign (unlocks the cover)"
      );
    }
    return CHAPTER_HINTS[u.chapterId] ?? "Finish the campaign finale";
  }
  return "Locked";
}

/** Close any open lock-tip popovers. */
function closeLockTips(except?: HTMLElement): void {
  document.querySelectorAll(".gallery-lock-label.is-open").forEach((el) => {
    if (el !== except) el.classList.remove("is-open");
  });
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function progressHtml(unlocked: number, total: number): string {
  const pct = total > 0 ? Math.round((unlocked / total) * 100) : 0;
  return `
    <div class="gallery-progress" role="img" aria-label="${unlocked} of ${total} unlocked">
      <div class="gallery-progress-track">
        <div class="gallery-progress-fill" style="width:${pct}%"></div>
      </div>
      <span class="gallery-progress-label">${unlocked} / ${total}</span>
    </div>
  `;
}

/**
 * Most recently unlocked pieces (collection.unlockedArt is append-order).
 * Prefer chapter keepsakes when filling the strip so starter bulk-grants
 * don't dominate every slot.
 */
function recentUnlocked(limit = RECENT_LIMIT): GalleryPiece[] {
  const order = loadCollection().unlockedArt;
  const chapter: GalleryPiece[] = [];
  const verses: GalleryPiece[] = [];
  for (let i = order.length - 1; i >= 0; i--) {
    const p = pieceById.get(order[i]!);
    if (!p || !isArtUnlocked(p.id)) continue;
    if (p.group === "chapter") chapter.push(p);
    else verses.push(p);
  }
  const out: GalleryPiece[] = [];
  for (const p of chapter) {
    if (out.length >= limit) break;
    out.push(p);
  }
  for (const p of verses) {
    if (out.length >= limit) break;
    out.push(p);
  }
  return out;
}

type TileOpts = {
  hero?: boolean;
  compact?: boolean;
  /**
   * Force eager image load. Use for above-the-fold / horizontal strips —
   * `loading=lazy` often never fires in overflow-x carousels until hover.
   */
  eager?: boolean;
  /** Parent list for lightbox navigation. */
  navList?: readonly GalleryPiece[];
};

function renderTile(
  piece: GalleryPiece,
  container: HTMLElement,
  opts: TileOpts = {},
): void {
  const unlocked = isArtUnlocked(piece.id);
  const btn = document.createElement(unlocked ? "button" : "div");
  if (unlocked) (btn as HTMLButtonElement).type = "button";
  btn.className =
    "gallery-tile" +
    (unlocked ? "" : " is-locked") +
    (opts.hero ? " is-hero" : "") +
    (opts.compact ? " is-compact" : "") +
    (piece.group === "verse" ? " is-verse" : " is-chapter");
  btn.setAttribute("role", "listitem");
  if (unlocked) {
    btn.setAttribute("aria-label", `View ${piece.title}`);
  } else {
    btn.setAttribute("aria-label", `Locked. ${hintFor(piece)}`);
  }

  const frame = document.createElement("div");
  frame.className =
    "gallery-frame" +
    (unlocked ? "" : " is-locked-frame") +
    (piece.group === "verse" ? " frame-verse" : " frame-chapter");

  // Clip shell keeps silhouettes rounded; frame itself can overflow lock tips.
  const clip = document.createElement("div");
  clip.className = "gallery-frame-clip";

  const img = document.createElement("img");
  img.src = piece.src;
  img.alt = unlocked ? piece.title : "";
  // Horizontal strips + section heroes: eager. Lazy often stalls in overflow-x
  // carousels until a hover/reflow re-checks intersection.
  img.loading = opts.eager || opts.hero || opts.compact ? "eager" : "lazy";
  img.decoding = "async";
  img.draggable = false;
  if (!unlocked) {
    img.className = "gallery-silhouette";
    img.setAttribute("aria-hidden", "true");
  }
  clip.appendChild(img);

  if (!unlocked) {
    const veil = document.createElement("span");
    veil.className = "gallery-veil";
    veil.setAttribute("aria-hidden", "true");
    clip.appendChild(veil);
  }
  frame.appendChild(clip);

  if (!unlocked) {
    const hint = hintFor(piece);
    const lock = document.createElement("button");
    lock.type = "button";
    lock.className = "gallery-lock-label";
    lock.textContent = "Locked";
    lock.dataset.tip = hint;
    lock.title = hint;
    lock.setAttribute("aria-label", `Locked. ${hint}`);
    lock.setAttribute("aria-expanded", "false");
    lock.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const open = !lock.classList.contains("is-open");
      closeLockTips(lock);
      lock.classList.toggle("is-open", open);
      lock.setAttribute("aria-expanded", open ? "true" : "false");
    });
    lock.addEventListener("blur", () => {
      // Delay so a second tip click still works; close when focus leaves.
      window.setTimeout(() => {
        if (document.activeElement !== lock) {
          lock.classList.remove("is-open");
          lock.setAttribute("aria-expanded", "false");
        }
      }, 0);
    });
    frame.appendChild(lock);
  }

  const meta = document.createElement("div");
  meta.className = "gallery-meta";
  const title = document.createElement("span");
  title.className = "gallery-title";
  title.textContent = unlocked ? piece.title : "???";
  meta.appendChild(title);

  if (unlocked && piece.caption && !opts.compact) {
    const cap = document.createElement("span");
    cap.className = "gallery-caption";
    cap.textContent = piece.caption;
    meta.appendChild(cap);
  } else if (!unlocked) {
    const cap = document.createElement("span");
    cap.className = "gallery-caption";
    cap.textContent = hintFor(piece);
    meta.appendChild(cap);
  }

  btn.append(frame, meta);

  if (unlocked) {
    btn.addEventListener("click", () => {
      const list = opts.navList ?? [piece];
      const index = Math.max(
        0,
        list.findIndex((p) => p.id === piece.id),
      );
      openLightbox(list, index);
    });
  }

  container.appendChild(btn);
}

function ensureLightbox(): HTMLElement {
  let root = document.getElementById("gallery-lightbox");
  if (root) return root;

  root = document.createElement("div");
  root.id = "gallery-lightbox";
  root.className = "gallery-lightbox hidden";
  root.innerHTML = `
    <div class="gallery-lightbox-inner" role="dialog" aria-modal="true" aria-label="Gallery viewer">
      <button type="button" class="gallery-lightbox-nav gallery-lightbox-prev" data-prev aria-label="Previous">‹</button>
      <div class="gallery-lightbox-stage">
        <div class="gallery-lightbox-plaque">
          <img data-img alt="" />
        </div>
        <div class="gallery-lightbox-copy">
          <p class="gallery-lightbox-caption" data-cap></p>
          <p class="gallery-lightbox-verse" data-verse hidden></p>
          <p class="gallery-lightbox-counter" data-counter hidden></p>
        </div>
        <div class="gallery-lightbox-actions">
          <button type="button" class="btn secondary gallery-lightbox-close" data-close>Close</button>
        </div>
      </div>
      <button type="button" class="gallery-lightbox-nav gallery-lightbox-next" data-next aria-label="Next">›</button>
    </div>
  `;
  document.body.appendChild(root);

  root.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    if (t === root || t.closest("[data-close]")) {
      closeLightbox();
      return;
    }
    if (t.closest("[data-prev]")) {
      stepLightbox(-1);
      return;
    }
    if (t.closest("[data-next]")) {
      stepLightbox(1);
    }
  });

  window.addEventListener("keydown", (e) => {
    if (root!.classList.contains("hidden")) return;
    if (e.key === "Escape") {
      closeLightbox();
      return;
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      stepLightbox(-1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      stepLightbox(1);
    }
  });

  return root;
}

function closeLightbox(): void {
  const root = document.getElementById("gallery-lightbox");
  if (root) root.classList.add("hidden");
  lightboxCtx = null;
}

function stepLightbox(delta: number): void {
  if (!lightboxCtx || lightboxCtx.pieces.length < 2) return;
  const n = lightboxCtx.pieces.length;
  const next = (lightboxCtx.index + delta + n) % n;
  openLightbox(lightboxCtx.pieces, next);
}

function openLightbox(
  pieces: readonly GalleryPiece[],
  index: number,
): void {
  const unlocked = pieces.filter((p) => isArtUnlocked(p.id));
  if (!unlocked.length) return;

  let i = unlocked.findIndex((p) => p.id === pieces[index]?.id);
  if (i < 0) i = Math.min(Math.max(0, index), unlocked.length - 1);
  lightboxCtx = { pieces: unlocked, index: i };

  const piece = unlocked[i]!;
  const root = ensureLightbox();
  const img = root.querySelector("[data-img]") as HTMLImageElement;
  const cap = root.querySelector("[data-cap]") as HTMLElement;
  const verseEl = root.querySelector("[data-verse]") as HTMLElement;
  const counter = root.querySelector("[data-counter]") as HTMLElement;
  const prevBtn = root.querySelector("[data-prev]") as HTMLButtonElement;
  const nextBtn = root.querySelector("[data-next]") as HTMLButtonElement;
  const plaque = root.querySelector(".gallery-lightbox-plaque") as HTMLElement;

  img.src = piece.src;
  img.alt = piece.title;
  plaque.classList.toggle("is-verse", piece.group === "verse");
  plaque.classList.toggle("is-chapter", piece.group === "chapter");

  const verse = verseForPiece(piece);
  if (verse) {
    cap.textContent = verse.reference;
    verseEl.textContent = verse.textKjv;
    verseEl.hidden = false;
  } else {
    cap.textContent = piece.caption
      ? `${piece.title} — ${piece.caption}`
      : piece.title;
    verseEl.textContent = "";
    verseEl.hidden = true;
  }

  if (unlocked.length > 1) {
    counter.hidden = false;
    counter.textContent = `${i + 1} / ${unlocked.length}`;
    prevBtn.hidden = false;
    nextBtn.hidden = false;
  } else {
    counter.hidden = true;
    prevBtn.hidden = true;
    nextBtn.hidden = true;
  }

  root.classList.remove("hidden");
}

function renderRecent(host: HTMLElement): void {
  const recent = recentUnlocked(RECENT_LIMIT);
  // Hide if nothing beyond starter bulk — require at least one chapter keep
  // or more than 3 unlocks total for the strip to feel intentional.
  const chapterRecent = recent.filter((p) => p.group === "chapter");
  if (!recent.length || (chapterRecent.length === 0 && recent.length < 4)) {
    host.hidden = true;
    host.replaceChildren();
    return;
  }

  host.hidden = false;
  host.replaceChildren();

  const head = document.createElement("div");
  head.className = "gallery-section-head";
  head.innerHTML = `
    <div class="gallery-section-head-text">
      <h2 id="gallery-recent-title" class="gallery-section-title">Recently remembered</h2>
    </div>
  `;

  const strip = document.createElement("div");
  strip.className = "gallery-recent-strip";
  strip.setAttribute("role", "list");

  const navList = recent;
  for (const p of recent) {
    renderTile(p, strip, { compact: true, eager: true, navList });
  }

  host.append(head, strip);
}

function renderEmptyCta(el: HTMLElement): void {
  const chapterUnlocked = GALLERY_CHAPTER_PIECES.filter((p) =>
    isArtUnlocked(p.id),
  ).length;
  if (chapterUnlocked > 0) {
    el.hidden = true;
    el.replaceChildren();
    return;
  }

  el.hidden = false;
  el.innerHTML = `
    <span class="gallery-empty-cta-icon" aria-hidden="true">◇</span>
    <span class="gallery-empty-cta-copy">
      <strong>Chapter keepsakes await.</strong>
      Clear campaign stages to unveil cinematic art — and complete a campaign for its cover plate.
    </span>
    <a class="btn secondary gallery-empty-cta-btn" href="/prototypes/proclaim/?mode=campaign">Play campaign →</a>
  `;
}

function renderChapterSections(host: HTMLElement): void {
  host.replaceChildren();
  for (const section of GALLERY_CHAPTER_SECTIONS) {
    const pieces = section.pieces;
    const unlockedN = pieces.filter((p) => isArtUnlocked(p.id)).length;
    const complete = unlockedN === pieces.length && pieces.length > 0;
    const featured =
      pieces.find((p) => p.featured) ?? pieces[0] ?? null;
    const bannerSrc = featured?.src;
    const bannerUnlocked = featured ? isArtUnlocked(featured.id) : false;

    const el = document.createElement("section");
    el.className =
      "gallery-section gallery-section-chapter" +
      (complete ? " is-complete" : "");
    el.setAttribute("aria-labelledby", `gallery-chapter-${section.id}`);

    // Mood banner from campaign cover art
    if (bannerSrc) {
      const banner = document.createElement("div");
      banner.className =
        "gallery-mood" + (bannerUnlocked ? " is-revealed" : " is-veiled");
      banner.setAttribute("aria-hidden", "true");
      banner.innerHTML = `
        <img class="gallery-mood-img" src="${escapeHtml(bannerSrc)}" alt="" loading="lazy" decoding="async" />
        <div class="gallery-mood-fade"></div>
      `;
      el.appendChild(banner);
    }

    const head = document.createElement("div");
    head.className = "gallery-section-head";
    head.innerHTML = `
      <div class="gallery-section-head-text">
        <div class="gallery-section-title-row">
          <h2 id="gallery-chapter-${section.id}" class="gallery-section-title">${escapeHtml(section.title)}</h2>
          ${
            complete
              ? `<span class="gallery-section-seal" title="Chapter complete">Complete</span>`
              : ""
          }
        </div>
        ${
          section.lead
            ? `<p class="gallery-section-lead">${escapeHtml(section.lead)}</p>`
            : ""
        }
      </div>
      ${progressHtml(unlockedN, pieces.length)}
    `;

    const gridPieces = featured
      ? pieces.filter((p) => p.id !== featured.id)
      : pieces;
    const navList = pieces;

    const body = document.createElement("div");
    body.className = "gallery-section-body";

    if (featured) {
      const heroWrap = document.createElement("div");
      heroWrap.className = "gallery-hero";
      heroWrap.setAttribute("role", "list");
      renderTile(featured, heroWrap, { hero: true, navList });
      body.appendChild(heroWrap);
    }

    if (unlockedN === 0) {
      const empty = document.createElement("p");
      empty.className = "gallery-section-empty";
      empty.textContent =
        "No keepsakes from this campaign yet — clear a stage to unveil the first plate.";
      body.appendChild(empty);
    }

    const grid = document.createElement("div");
    grid.className = "gallery-grid gallery-grid-chapter";
    grid.setAttribute("role", "list");
    for (const p of gridPieces) {
      renderTile(p, grid, { navList });
    }
    body.appendChild(grid);

    el.append(head, body);
    host.appendChild(el);
  }
}

function renderVerseSection(verseRoot: HTMLElement): void {
  const section = verseRoot.closest(".gallery-section");
  const unlockedN = versePieces.filter((p) => isArtUnlocked(p.id)).length;
  const head = section?.querySelector(".gallery-section-head");
  if (head) {
    const existing = head.querySelector(".gallery-progress");
    if (existing) existing.remove();
    head.insertAdjacentHTML(
      "beforeend",
      progressHtml(unlockedN, versePieces.length),
    );
  }

  // Group by catalog category
  const byCat = new Map<string, GalleryPiece[]>();
  for (const p of versePieces) {
    const v = verseForPiece(p);
    const cat = v?.category ?? "other";
    let list = byCat.get(cat);
    if (!list) {
      list = [];
      byCat.set(cat, list);
    }
    list.push(p);
  }

  const orderedCats = [
    ...VERSE_CATEGORY_ORDER.filter((c) => byCat.has(c)),
    ...[...byCat.keys()].filter(
      (c) => !(VERSE_CATEGORY_ORDER as readonly string[]).includes(c),
    ),
  ];

  verseRoot.replaceChildren();
  for (const cat of orderedCats) {
    const pieces = byCat.get(cat)!;
    const group = document.createElement("div");
    group.className = "gallery-verse-group";
    group.setAttribute("role", "group");
    group.setAttribute("aria-label", verseCategoryLabel(cat));

    const label = document.createElement("h3");
    label.className = "gallery-verse-group-title";
    const u = pieces.filter((p) => isArtUnlocked(p.id)).length;
    label.innerHTML = `
      <span>${escapeHtml(verseCategoryLabel(cat))}</span>
      <span class="gallery-verse-group-count">${u} / ${pieces.length}</span>
    `;

    const grid = document.createElement("div");
    grid.className = "gallery-grid gallery-grid-verse";
    grid.setAttribute("role", "list");
    for (const p of pieces) {
      renderTile(p, grid, { navList: versePieces });
    }

    group.append(label, grid);
    verseRoot.appendChild(group);
  }
}

function render(): void {
  const recentHost = document.getElementById("gallery-recent");
  const emptyCta = document.getElementById("gallery-empty-cta");
  const chaptersHost = document.getElementById("gallery-chapters");
  const verseRoot = document.getElementById("gallery-verses");
  const stats = document.getElementById("gallery-stats");
  if (!chaptersHost || !verseRoot) return;

  if (recentHost) renderRecent(recentHost);
  if (emptyCta) renderEmptyCta(emptyCta);
  renderChapterSections(chaptersHost);
  renderVerseSection(verseRoot);

  if (stats) {
    const unlocked = allPieces.filter((p) => isArtUnlocked(p.id)).length;
    const owned = loadCollection();
    const kinds = Object.keys(owned.ownedCounts).length;
    stats.innerHTML = `
      <span class="gallery-stats-main">Gallery <strong>${unlocked}</strong> / ${allPieces.length}</span>
      <span class="gallery-stats-sep">·</span>
      <span class="gallery-stats-sub">Collection ${kinds} verse types · device save only</span>
    `;
  }
}

// Dismiss lock tips when clicking elsewhere
document.addEventListener(
  "pointerdown",
  (e) => {
    const t = e.target as HTMLElement | null;
    if (t?.closest?.(".gallery-lock-label")) return;
    closeLockTips();
  },
  true,
);

render();
